import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useParticipants } from "@livekit/components-react";
import { Hash, Link, UserPlus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { videoService } from "../api/video.api";
import { usersService } from "../../chat/api/users";
import type { CursorPage, User } from "../../chat/api/types";
import { useMe } from "../../chat/hooks/useChatRole";
import {
  ORG_DIRECTORY_MIN_QUERY_LENGTH,
  useOrgDirectorySearch,
} from "../hooks/useOrgDirectorySearch";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUGGESTION_LIMIT = 8;

interface InCallShareDialogProps {
  roomId: string;
  /** Same URL the meeting-info popover copies (`room.join_url ?? …`). */
  shareLink: string;
  /** Room.join_code — the row shows a placeholder while it loads and hides
   *  once loaded if the room has none. */
  meetingCode?: string | null;
  meetingCodeLoading?: boolean;
  onClose: () => void;
}

type InviteState = "pending" | "invited";

/** One row in the people list — a directory user, or a bare email the
 *  caller typed that matched nobody in the organisation. */
interface PersonRow {
  key: string;
  name: string;
  email: string;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

/**
 * "Invite people" card opened from the in-call bar's Share button.
 *
 * Inviting reuses the in-call email invite (`POST /rooms/{id}/invite/`, the
 * same call InviteByEmailComposer makes) with the person's directory email —
 * no new endpoint. People come from the org directory on chat
 * (`useOrgDirectorySearch`, 2+ characters); with nothing typed the card shows
 * the first few directory entries as suggestions.
 */
export function InCallShareDialog({
  roomId,
  shareLink,
  meetingCode,
  meetingCodeLoading = false,
  onClose,
}: InCallShareDialogProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [inviteState, setInviteState] = useState<Record<string, InviteState>>({});
  const { data: me } = useMe();
  const participants = useParticipants();

  const trimmed = search.trim();
  const isSearching = trimmed.length >= ORG_DIRECTORY_MIN_QUERY_LENGTH;
  const directory = useOrgDirectorySearch(trimmed);
  const suggestions = useQuery<CursorPage<User>>({
    queryKey: ["call", "directory", "suggestions"],
    queryFn: () => usersService.list({ limit: SUGGESTION_LIMIT }),
    staleTime: 60_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const joinedSubs = useMemo(
    () => new Set(participants.map((p) => p.identity)),
    [participants],
  );

  const rows: PersonRow[] = useMemo(() => {
    const source = isSearching
      ? (directory.data?.results ?? [])
      : (suggestions.data?.results ?? []).filter((u) => {
          if (!trimmed) return true;
          const q = trimmed.toLowerCase();
          return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
        });
    const people = source
      .filter((u) => u.sub !== me?.sub && !joinedSubs.has(u.sub) && Boolean(u.email))
      .map((u) => ({ key: u.sub, name: u.name || u.email, email: u.email }));
    // A typed address that matches nobody can still be invited by email.
    if (EMAIL_RE.test(trimmed) && !people.some((p) => p.email.toLowerCase() === trimmed.toLowerCase())) {
      people.push({ key: `email:${trimmed.toLowerCase()}`, name: trimmed, email: trimmed });
    }
    return people;
  }, [isSearching, directory.data, suggestions.data, trimmed, me?.sub, joinedSubs]);

  const loading = isSearching ? directory.isFetching && !directory.data : suggestions.isLoading;

  const handleInvite = async (row: PersonRow) => {
    if (inviteState[row.key]) return;
    setInviteState((s) => ({ ...s, [row.key]: "pending" }));
    try {
      const result = await videoService.inviteToRoom(roomId, { emails: [row.email] });
      if (result.sent > 0 && result.errors.length === 0) {
        setInviteState((s) => ({ ...s, [row.key]: "invited" }));
        toast.success(t("call.share.invitedToast", { name: row.name }));
        return;
      }
      throw new Error(result.errors[0]?.reason ?? "send_failed");
    } catch {
      setInviteState((s) => {
        const next = { ...s };
        delete next[row.key];
        return next;
      });
      toast.error(t("call.share.inviteError", { name: row.name }));
    }
  };

  const copy = async (text: string, copiedToast: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }
    toast.success(copiedToast);
  };

  const showCode = Boolean(meetingCode) || meetingCodeLoading;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-md anim-fade"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="in-call-share-title"
        className="relative w-full max-w-[440px] max-h-[calc(100vh-2rem)] flex flex-col bg-background border border-border shadow-2xl rounded-[28px] overflow-hidden anim-scale-in"
      >
        <div className="px-6 pt-6 pb-4 border-b border-border flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
              {t("call.share.eyebrow")}
            </p>
            <h2 id="in-call-share-title" className="text-[22px] leading-tight font-black text-foreground">
              {t("call.share.title")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            title={t("call.share.close")}
            aria-label={t("call.share.close")}
            className="p-2 rounded-xl hover:bg-muted text-muted-foreground transition-colors shrink-0"
          >
            <X className="size-[18px]" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto no-scrollbar">
          <div>
            <label className="flex items-center gap-3 h-12 px-4 rounded-2xl border border-border bg-background focus-within:border-primary transition-colors">
              <UserPlus className="size-[18px] text-muted-foreground shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("call.share.searchPlaceholder")}
                aria-label={t("call.share.searchPlaceholder")}
                autoFocus
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </label>

            <ul className="mt-3 space-y-1 max-h-60 overflow-y-auto no-scrollbar" aria-live="polite">
              {loading && (
                <li className="px-1 py-2 text-xs text-muted-foreground">{t("call.share.searching")}</li>
              )}
              {!loading && rows.length === 0 && (
                <li className="px-1 py-2 text-xs text-muted-foreground">
                  {trimmed ? t("call.share.noResults", { query: trimmed }) : t("call.share.typeToSearch")}
                </li>
              )}
              {rows.map((row) => {
                const state = inviteState[row.key];
                return (
                  <li key={row.key} className="flex items-center gap-3 px-1 py-1.5">
                    <span className="size-9 shrink-0 rounded-full bg-neutral-800 text-white text-xs font-bold flex items-center justify-center select-none">
                      {initialsOf(row.name)}
                    </span>
                    <span className="flex-1 min-w-0 text-sm font-bold text-foreground truncate" title={row.email}>
                      {row.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => void handleInvite(row)}
                      disabled={Boolean(state)}
                      aria-label={
                        state === "invited"
                          ? t("call.share.invitedName", { name: row.name })
                          : t("call.share.inviteName", { name: row.name })
                      }
                      className={`h-8 px-3.5 rounded-xl text-xs font-bold shrink-0 transition-opacity ${
                        state === "invited"
                          ? "bg-muted text-muted-foreground cursor-default"
                          : "bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60"
                      }`}
                    >
                      {state === "invited"
                        ? t("call.share.invited")
                        : state === "pending"
                          ? t("call.share.inviting")
                          : t("call.share.invite")}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
              {t("call.share.meetingLink")}
            </p>
            <div className="flex items-center gap-2 px-4 h-12 bg-muted/50 rounded-2xl">
              <Link className="size-[15px] text-muted-foreground shrink-0" />
              <span dir="ltr" className="flex-1 min-w-0 text-sm font-medium truncate text-foreground text-start">
                {shareLink}
              </span>
              <button
                type="button"
                onClick={() => void copy(shareLink, t("call.share.linkCopiedToast"))}
                aria-label={t("call.share.copyLink")}
                className="text-xs font-bold text-primary hover:underline shrink-0"
              >
                {t("call.share.copy")}
              </button>
            </div>
          </div>

          {showCode && (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
                {t("call.share.meetingCode")}
              </p>
              <div className="flex items-center gap-2 px-4 h-12 bg-muted/50 rounded-2xl">
                <Hash className="size-[15px] text-muted-foreground shrink-0" />
                {meetingCode ? (
                  <>
                    <span dir="ltr" className="flex-1 min-w-0 text-sm font-mono font-bold tracking-wider text-foreground truncate text-start">
                      {meetingCode}
                    </span>
                    <button
                      type="button"
                      onClick={() => void copy(meetingCode, t("call.share.codeCopiedToast"))}
                      aria-label={t("call.share.copyCode")}
                      className="text-xs font-bold text-primary hover:underline shrink-0"
                    >
                      {t("call.share.copy")}
                    </button>
                  </>
                ) : (
                  <span
                    role="status"
                    aria-label={t("call.controls.loadingCode")}
                    className="flex-1 h-4 rounded bg-muted animate-pulse"
                  />
                )}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="w-full h-12 bg-primary text-primary-foreground font-bold rounded-2xl text-sm hover:opacity-90 transition-opacity"
          >
            {t("call.share.done")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
